<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                {{ isViewMode ? "View Asset Acquisition" : (editingAcquisition ? "Edit Asset Acquisition" : "Add New Asset Acquisition") }}
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-form @submit.prevent="submit" ref="form">
                  <v-row class="pa-1">
                    <!-- Acquisition Date -->
                    <v-col md="4" cols="12">
                      <v-date-input
                        input-format="dd/mm/yyyy"
                        v-model="asset_acquisition_date.value.value"
                        label="Acquisition Date *"
                        density="compact"
                        prepend-icon=""
                        prepend-inner-icon="mdi-calendar"
                        variant="outlined"
                        bg-color="grey-lighten-4"
                        :error-messages="asset_acquisition_date.errorMessage.value"
                        :disabled="isViewMode"
                        autocomplete="off"
                        clearable
                      ></v-date-input>
                    </v-col>

                    <!-- Purchase Order (Optional) -->
                    <v-col md="4" cols="12">
                      <v-autocomplete
                        v-model="pur_order_master_id.value.value"
                        label="Purchase Order (Optional)"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        :items="purchaseOrders"
                        :item-title="(item) => `${item.code} - ${item.supplier_name || item.supplier?.supplier_name || 'N/A'}`"
                        item-value="id"
                        :error-messages="pur_order_master_id.errorMessage.value"
                        :loading="poLoading"
                        :disabled="isViewMode"
                        clearable
                      
                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
>
                        <template v-slot:item="{ props, item }">
                          <v-list-item v-bind="props">
                            <template v-slot:title>
                              <div>
                                <strong>PO: {{ item.raw.code }}</strong>
                              </div>
                              <div class="text-caption text-grey">
                                Supplier: {{ item.raw.supplier_name || item.raw.supplier?.supplier_name || 'N/A' }}
                              </div>
                            </template>
                          </v-list-item>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <!-- Remarks -->
                    <v-col md="4" cols="12">
                      <v-textarea
                        v-model="remarks.value.value"
                        label="Remarks"
                        variant="outlined"
                        density="compact"
                        bg-color="grey-lighten-4"
                        rows="2"
                        :error-messages="remarks.errorMessage.value"
                        :disabled="isViewMode"
                      ></v-textarea>
                    </v-col>
                  </v-row>

                  <!-- Assets Acquisition Section -->
                  <v-row class="pa-2">
                    <v-col cols="12">
                      <v-card variant="outlined" class="pa-4">
                        <v-card-title class="pa-2">
                          <span class="text-h6">Asset Items</span>
                        </v-card-title>
                        <v-card-text>
                          <!-- Empty State -->
                          <v-alert
                            v-if="acquisitionAssets.length === 0"
                            type="info"
                            variant="tonal"
                            class="mb-4"
                          >
                            <div class="d-flex flex-column align-center py-4">
                              <v-icon size="64" color="grey-darken-1">mdi-package-variant-closed</v-icon>
                              <p class="text-h6 mt-4 mb-2">No assets added yet</p>
                              <p class="text-body-2 text-grey">
                                Please select a Purchase Order above to load products
                              </p>
                            </div>
                          </v-alert>

                          <!-- Asset Cards -->
                          <div v-if="acquisitionAssets.length > 0">
                            <v-card
                              v-for="(asset, index) in acquisitionAssets"
                              :key="index"
                              elevation="2"
                              class="mb-4"
                            >
                              <v-card-title class="asset-card-header d-flex align-center pa-4">
                                <v-avatar color="primary" size="36" class="mr-3">
                                  <span class="text-white">{{ index + 1 }}</span>
                                </v-avatar>
                                <div class="flex-grow-1">
                                  <div class="text-subtitle-1 font-weight-medium">
                                    {{ getProductName(asset.product_id) || 'Select Product' }}
                                  </div>
                                  <div class="text-caption text-grey">
                                    Category: {{ getCategoryName(asset.asset_category_id) || 'Not Selected' }} | 
                                    Qty: {{ asset.quantity || 0 }} | 
                                    Cost: {{ formatCurrency(asset.purchase_cost) }}
                                  </div>
                                </div>
                                <v-btn
                                  v-if="!isViewMode"
                                  icon="mdi-delete"
                                  size="small"
                                  color="error"
                                  variant="text"
                                  @click="removeAssetRow(index)"
                                ></v-btn>
                              </v-card-title>

                              <v-card-text class="pa-4">
                                <v-form>
                                  <v-row md="6" cols="12">
                                    <!-- Product -->
                                    <!-- <v-col md="4" cols="12">
                                      <v-autocomplete
                                        v-model="asset.product_id"
                                        label="Product *"
                                        density="compact"
                                        variant="outlined"
                                        bg-color="grey-lighten-4"
                                        :items="pur_order_master_id.value.value ? filteredProducts : products"
                                        :item-title="(item) => `${item.code} - ${item.name}`"
                                        item-value="id"
                                        :loading="productLoading"
                                        :disabled="isViewMode || pur_order_master_id.value.value"
                                        prepend-inner-icon="mdi-cube-outline"
                                      
                                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-autocomplete>
                                    </v-col> -->

                                    <!-- Category -->
                                    <v-col md="4" cols="12">
                                      <v-autocomplete
                                        v-model="asset.asset_category_id"
                                        label="Asset Category *"
                                        density="compact"
                                        variant="outlined"
                                        bg-color="grey-lighten-4"
                                        :items="categories"
                                        item-title="name"
                                        item-value="id"
                                        :loading="categoryLoading"
                                        :disabled="isViewMode"
                                        prepend-inner-icon="mdi-shape-outline"
                                      
                                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-autocomplete>
                                    </v-col>

                                    <!-- Location -->
                                    <v-col md="4" cols="12">
                                      <v-autocomplete
                                        v-model="asset.asset_location_id"
                                        label="Asset Location *"
                                        density="compact"
                                        variant="outlined"
                                        bg-color="grey-lighten-4"
                                        :items="locations"
                                        item-title="name"
                                        item-value="id"
                                        :loading="locationLoading"
                                        :disabled="isViewMode"
                                        prepend-inner-icon="mdi-map-marker-outline"
                                      
                                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-autocomplete>
                                    </v-col>

                                    <!-- Status -->
                                    <v-col md="4" cols="12">
                                      <v-autocomplete
                                        v-model="asset.asset_status_id"
                                        label="Asset Status *"
                                        density="compact"
                                        variant="outlined"
                                        bg-color="grey-lighten-4"
                                        :items="statuses"
                                        item-title="name"
                                        item-value="id"
                                        :loading="statusLoading"
                                        :disabled="isViewMode"
                                        prepend-inner-icon="mdi-information-outline"
                                      
                                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-autocomplete>
                                    </v-col>

                                    <!-- Depreciation Method -->
                                    <v-col md="4" cols="12">
                                      <v-autocomplete
                                        v-model="asset.asset_depreciation_method_id"
                                        label="Depreciation Method *"
                                        density="compact"
                                        variant="outlined"
                                        bg-color="grey-lighten-4"
                                        :items="depreciationMethods"
                                        item-title="name"
                                        item-value="id"
                                        :loading="depMethodLoading"
                                        :disabled="isViewMode"
                                        prepend-inner-icon="mdi-chart-line"
                                        @update:model-value="onDepreciationMethodChange(asset, $event)"
                                      
                                          :menu-props="{ contentClass: 'my-custom-dropdown' }"
></v-autocomplete>
                                    </v-col>

                                    <!-- Depreciation Start Date -->
                                    <v-col md="4" cols="12">
                                      <v-date-input
                                        input-format="dd/mm/yyyy"
                                        v-model="asset.depreciation_start_date"
                                        label="Depreciation Start Date *"
                                        density="compact"
                                        prepend-icon=""
                                        prepend-inner-icon="mdi-calendar"
                                        variant="outlined"
                                        bg-color="grey-lighten-4"
                                        :disabled="isViewMode"
                                      ></v-date-input>
                                    </v-col>

                                    <!-- Divider -->
                                    <v-col cols="12">
                                      <v-divider class="my-2"></v-divider>
                                      <div class="text-subtitle-2 text-grey mb-3">Financial Details</div>
                                    </v-col>

                                    <!-- Purchase Cost -->
                                    <v-col md="2" cols="12">
                                      <v-text-field
                                        v-model.number="asset.purchase_cost"
                                        label="Purchase Cost *"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        density="compact"
                                        variant="outlined"
                                        bg-color="grey-lighten-4"
                                        :disabled="isViewMode"
                                       
                                      ></v-text-field>
                                    </v-col>

                                    <!-- Salvage Value -->
                                    <v-col md="2" cols="12">
                                      <v-text-field
                                        v-model.number="asset.salvage_value"
                                        label="Salvage Value"
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        density="compact"
                                        variant="outlined"
                                        bg-color="grey-lighten-4"
                                        :disabled="isViewMode"
                                        prepend-inner-icon="mdi-cash"
                                      ></v-text-field>
                                    </v-col>

                                    <!-- Useful Life -->
                                    <v-col md="2" cols="12">
                                      <v-text-field
                                        v-model.number="asset.useful_life"
                                        label="Useful Life (Years) *"
                                        type="number"
                                        min="1"
                                        density="compact"
                                        variant="outlined"
                                        bg-color="grey-lighten-4"
                                        :disabled="isViewMode"
                                        prepend-inner-icon="mdi-timer-outline"
                                      ></v-text-field>
                                    </v-col>

                                    <!-- Quantity -->
                                    <v-col md="2" cols="12">
                                      <v-text-field
                                        v-model.number="asset.quantity"
                                        label="Quantity *"
                                        type="number"
                                        step="0.001"
                                        min="0.001"
                                        density="compact"
                                        variant="outlined"
                                        bg-color="grey-lighten-4"
                                        :disabled="isViewMode"
                                        prepend-inner-icon="mdi-counter"
                                      ></v-text-field>
                                    </v-col>

                                    <!-- Warranty Month -->
                                    <v-col md="3" cols="12">
                                      <v-text-field
                                        v-model.number="asset.warranty_month"
                                        label="Warranty (Months)"
                                        type="number"
                                        min="0"
                                        density="compact"
                                        variant="outlined"
                                        bg-color="grey-lighten-4"
                                        :disabled="isViewMode"
                                        prepend-inner-icon="mdi-shield-check-outline"
                                      ></v-text-field>
                                    </v-col>

                                    <!-- Units of Production Fields (conditional) -->
                                    <template v-if="isUnitsOfProduction(asset.asset_depreciation_method_id)">
                                      <v-col cols="12">
                                        <v-divider class="my-2"></v-divider>
                                        <div class="text-subtitle-2 text-grey mb-3">Units of Production Method</div>
                                      </v-col>

                                      <v-col md="6" cols="12">
                                        <v-text-field
                                          v-model.number="asset.total_units"
                                          label="Total Units"
                                          type="number"
                                          min="1"
                                          density="compact"
                                          variant="outlined"
                                          bg-color="grey-lighten-4"
                                          :disabled="isViewMode"
                                          prepend-inner-icon="mdi-sigma"
                                        ></v-text-field>
                                      </v-col>

                                      <v-col md="6" cols="12">
                                        <v-text-field
                                          v-model.number="asset.per_period_unit"
                                          label="Per Period Unit"
                                          type="number"
                                          min="0"
                                          density="compact"
                                          variant="outlined"
                                          bg-color="grey-lighten-4"
                                          :disabled="isViewMode"
                                          prepend-inner-icon="mdi-timeline-clock-outline"
                                        ></v-text-field>
                                      </v-col>
                                    </template>
                                  </v-row>
                                </v-form>
                              </v-card-text>
                            </v-card>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>

                  <!-- Action Buttons -->
                  <v-row class="pa-2">
                    <v-col cols="12">
                      <div class="d-flex justify-end flex-wrap" style="gap: 12px;">

                        <template v-if="!isViewMode">
                          <v-btn
                            class="bg-grey-darken-1 me-2"
                            variant="flat"
                            type="button"
                            :loading="saving && currentAction === 0"
                            :disabled="saving"
                            @click="submitForm(0)"
                          >
                            DRAFT
                          </v-btn>
                          <v-btn
                            color="primary"
                            type="button"
                            :loading="saving && currentAction === 1"
                            :disabled="saving && currentAction !== 1"
                            prepend-icon="mdi-content-save"
                            @click="submitForm(1)"
                          >
                            {{ editingAcquisition ? "Update & Post" : "POST" }}
                          </v-btn>
                        </template>
                      </div>
                    </v-col>
                  </v-row>
                </v-form>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { ref, computed } from "vue";
import { toast } from "vue3-toastify";
import { 
  getProductList, 
  getAssetCategoryList, 
  getAssetLocationsList, 
  getAssetDepreciationMethodList, 
  getAssetStatusTypesList,
  getPurOrderList,
  getPurOrderById
} from "@/store/api";
import Axios from "axios";
import formattedDate from "@/plugins/dateFormatter";

export default {
  components: {},
  data() {
    return {
      panel: [0],
      saving: false,
      currentAction: null,
      productLoading: false,
      categoryLoading: false,
      locationLoading: false,
      depMethodLoading: false,
      statusLoading: false,
      poLoading: false,
      acquisitionAssets: [],
      products: [],
      filteredProducts: [], // Products from selected PO
      categories: [],
      locations: [],
      depreciationMethods: [],
      statuses: [],
      purchaseOrders: [],
      isViewMode: false,
    };
  },
  computed: {
    editingAcquisition() {
      return this.$store.getters.getEditingAssetAcquisition;
    },
    viewingAcquisition() {
      return this.$store.getters.getViewingAssetAcquisition;
    },
  },
  watch: {
    editingAcquisition: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.loadEditDataById(newVal.id);
        }
      },
      immediate: true,
    },
    viewingAcquisition: {
      handler(newVal) {
        if (newVal && newVal.id) {
          this.isViewMode = true;
          this.loadEditDataById(newVal.id);
        }
      },
      immediate: true,
    },
    'pur_order_master_id.value.value': {
      handler(newVal, oldVal) {
        // Only load PO products if:
        // 1. Value changed
        // 2. Not in edit/view mode (to prevent overwriting existing data)
        // 3. New value is not null/undefined
        if (newVal && newVal !== oldVal && !this.editingAcquisition && !this.viewingAcquisition) {
          this.loadPurchaseOrderProducts(newVal);
        }
        // If cleared, clear the assets and filtered products
        if (!newVal && oldVal && !this.editingAcquisition && !this.viewingAcquisition) {
          this.acquisitionAssets = [];
          this.filteredProducts = [];
        }
      },
    },
  },
  created() {
    this.loadDropdownData();
    this.loadPurchaseOrders();
    // Check if view mode from route params
    if (this.$route.params.mode === 'view') {
      this.isViewMode = true;
    }
  },
  beforeUnmount() {
    // Clear the Vuex store state when component is unmounted
    // This ensures clean state when navigating to add new acquisition
    this.$store.commit("UNSET_EDITING_ASSET_ACQUISITION");
    this.$store.commit("UNSET_VIEW_ASSET_ACQUISITION");
    
    // Reset local component state
    this.isViewMode = false;
    this.acquisitionAssets = [];
    this.filteredProducts = [];
  },
  setup() {
    const store = useStore();

    const validationSchema = yup.object({
      asset_acquisition_date: yup.string().required("Acquisition date is required"),
      pur_order_master_id: yup.number().nullable(),
      remarks: yup.string().nullable(),
      status: yup.number().required("Status is required").oneOf([0, 1]),
    });

    const { handleSubmit, resetForm } = useForm({
      validationSchema,
    });

    const asset_acquisition_date = useField("asset_acquisition_date");
    const pur_order_master_id = useField("pur_order_master_id");
    const remarks = useField("remarks");
    const status = useField("status");

    // Set default status to draft
    status.value.value = 0;

    return {
      asset_acquisition_date,
      pur_order_master_id,
      remarks,
      status,
      handleSubmit,
      resetForm,
    };
  },
  methods: {
    formattedDate,
    async submitForm(statusValue) {
      if (this.isViewMode) {
        return;
      }
      this.currentAction = typeof statusValue !== "undefined" ? statusValue : 1;
      this.status.value.value = this.currentAction;
      try {
        await this.submit();
      } finally {
        this.currentAction = null;
      }
    },
    async submit() {
      try {
        // Check if at least one asset is added
        if (this.acquisitionAssets.length === 0) {
          toast.error("Please add at least one asset item");
          return;
        }

        // Validate each asset row
        for (let i = 0; i < this.acquisitionAssets.length; i++) {
          const asset = this.acquisitionAssets[i];
          if (!asset.product_id) {
            toast.error(`Row ${i + 1}: Product is required`);
            return;
          }
          if (!asset.asset_category_id) {
            toast.error(`Row ${i + 1}: Category is required`);
            return;
          }
          if (!asset.asset_location_id) {
            toast.error(`Row ${i + 1}: Location is required`);
            return;
          }
          if (!asset.asset_depreciation_method_id) {
            toast.error(`Row ${i + 1}: Depreciation method is required`);
            return;
          }
          if (!asset.asset_status_id) {
            toast.error(`Row ${i + 1}: Status is required`);
            return;
          }
          if (!asset.purchase_cost || asset.purchase_cost <= 0) {
            toast.error(`Row ${i + 1}: Purchase cost is required and must be greater than 0`);
            return;
          }
          if (!asset.useful_life || asset.useful_life <= 0) {
            toast.error(`Row ${i + 1}: Useful life is required and must be greater than 0`);
            return;
          }
          if (!asset.quantity || asset.quantity <= 0) {
            toast.error(`Row ${i + 1}: Quantity is required and must be greater than 0`);
            return;
          }
          if (!asset.depreciation_start_date) {
            toast.error(`Row ${i + 1}: Depreciation start date is required`);
            return;
          }
        }

        // Validate and submit
        await this.handleSubmit(async (values) => {
          this.saving = true;

          const acquisitionData = {
            asset_register_acquisition_master_id: this.editingAcquisition?.id || null,
            pur_order_master_id: values.pur_order_master_id || null,
            asset_acquisition_date: this.formattedDate(values.asset_acquisition_date),
            remarks: values.remarks || null,
            status: values.status,
            assets: this.acquisitionAssets.map((asset) => ({
              asset_acquisition_details_id: asset.asset_acquisition_details_id || null,
              product_id: asset.product_id,
              asset_category_id: asset.asset_category_id,
              asset_location_id: asset.asset_location_id,
              asset_depreciation_method_id: asset.asset_depreciation_method_id,
              asset_status_id: asset.asset_status_id,
              purchase_cost: asset.purchase_cost,
              salvage_value: asset.salvage_value || 0,
              useful_life: asset.useful_life,
              quantity: asset.quantity,
              warranty_month: asset.warranty_month ? parseInt(asset.warranty_month) : null,
              depreciation_start_date: this.formattedDate(asset.depreciation_start_date),
              total_units: asset.total_units || 1,
              per_period_unit: asset.per_period_unit || 0,
            })),
          };

          console.log('Sending acquisition data:', acquisitionData);

          try {
            const response = await this.$store.dispatch("saveUpdateAssetAcquisition", acquisitionData);
            if (response.data.success) {
              toast.success(response.data.message || "Asset acquisition saved successfully");
              this.$store.commit("UNSET_EDITING_ASSET_ACQUISITION");
              this.$store.commit("UNSET_VIEW_ASSET_ACQUISITION");
              this.$router.push({ name: "acquisition-purchase-records" });
            } else {
              toast.error(response.data.message || "Failed to save asset acquisition");
            }
          } catch (error) {
            console.error('Save error:', error);
            console.error('Error response:', error.response?.data);
            toast.error(error.response?.data?.message || "Failed to save asset acquisition");
          } finally {
            this.saving = false;
          }
        })();
      } catch (error) {
        console.error("Validation error:", error);
        this.saving = false;
      }
    },
    async loadDropdownData() {
      // Load all dropdown data in parallel
      await Promise.all([
        this.loadProducts(),
        this.loadCategories(),
        this.loadLocations(),
        this.loadDepreciationMethods(),
        this.loadStatuses(),
      ]);
    },
    async loadPurchaseOrders() {
      this.poLoading = true;
      try {
        const response = await Axios.post(getPurOrderList, {
          status: 1 // Only get posted/approved purchase orders
        });
        this.purchaseOrders = response.data.data || [];
      } catch (error) {
        console.error("Error loading purchase orders:", error);
        this.purchaseOrders = [];
      } finally {
        this.poLoading = false;
      }
    },
    async loadPurchaseOrderProducts(poId) {
      try {
        // Show loading indicator
        this.productLoading = true;
        
        const getPurOrderUrl = `${getPurOrderById}/${poId}`;
        const response = await Axios.post(getPurOrderUrl, {});
        
        if (response.data && response.data.data) {
          const poData = response.data.data;
          
          // Clear existing assets
          this.acquisitionAssets = [];
          this.filteredProducts = [];
          
          // Check if PO has details (backend returns 'purOrderDetails')
          if (poData.pur_order_details && poData.pur_order_details.length > 0) {
            // Extract products from PO details for filtered list
            this.filteredProducts = poData.pur_order_details.map((detail) => {
              // Find the product in products list to get full details
              const product = this.products.find(p => p.id === detail.product_id);
              return product || {
                id: detail.product_id,
                code: detail.product?.code || 'N/A',
                name: detail.product?.name || 'Unknown Product'
              };
            });

            // Create asset rows from PO details
            this.acquisitionAssets = poData.pur_order_details.map((detail) => ({
              asset_acquisition_details_id: null,
              product_id: detail.product_id,
              asset_category_id: null,
              asset_location_id: null,
              asset_depreciation_method_id: null,
              asset_status_id: null,
              purchase_cost: detail.rate || 0, // Use PO rate as purchase cost
              salvage_value: 0,
              useful_life: 1,
              quantity: detail.qty || 1, // Use PO qty
              warranty_month: null,
              depreciation_start_date: new Date().toISOString().split('T')[0],
              total_units: 1,
              per_period_unit: 0,
            }));
            
            toast.success(`Loaded ${this.acquisitionAssets.length} products from Purchase Order`);
          } else {
            toast.info("This Purchase Order has no products");
          }
        }
      } catch (error) {
        console.error("Error loading purchase order products:", error);
        toast.error("Failed to load products from Purchase Order");
      } finally {
        this.productLoading = false;
      }
    },
    async loadProducts() {
      this.productLoading = true;
      try {
        const response = await Axios.post(getProductList);
        this.products = response.data.data || [];
      } catch (error) {
        console.error("Error loading products:", error);
        this.products = [];
      } finally {
        this.productLoading = false;
      }
    },
    async loadCategories() {
      this.categoryLoading = true;
      try {
        const response = await Axios.get(getAssetCategoryList);
        this.categories = response.data.data || [];
      } catch (error) {
        console.error("Error loading categories:", error);
        this.categories = [];
      } finally {
        this.categoryLoading = false;
      }
    },
    async loadLocations() {
      this.locationLoading = true;
      try {
        const response = await Axios.get(getAssetLocationsList);
        this.locations = response.data.data || [];
      } catch (error) {
        console.error("Error loading locations:", error);
        this.locations = [];
      } finally {
        this.locationLoading = false;
      }
    },
    async loadDepreciationMethods() {
      this.depMethodLoading = true;
      try {
        const response = await Axios.get(getAssetDepreciationMethodList);
        this.depreciationMethods = response.data.data || [];
      } catch (error) {
        console.error("Error loading depreciation methods:", error);
        this.depreciationMethods = [];
      } finally {
        this.depMethodLoading = false;
      }
    },
    async loadStatuses() {
      this.statusLoading = true;
      try {
        const response = await Axios.get(getAssetStatusTypesList);
        this.statuses = response.data.data || [];
      } catch (error) {
        console.error("Error loading statuses:", error);
        this.statuses = [];
      } finally {
        this.statusLoading = false;
      }
    },
    async loadEditDataById(id) {
      try {
        const response = await this.$store.dispatch("getAssetAcquisitionById", id);
        if (response && response.data) {
          this.loadEditData(response.data);
        }
      } catch (error) {
        console.error("Error loading acquisition data:", error);
        toast.error("Failed to load acquisition data");
      }
    },
    loadEditData(acquisition) {
      if (acquisition) {
        this.asset_acquisition_date.value.value = acquisition.asset_acquisition_date;
        this.pur_order_master_id.value.value = acquisition.pur_order_master_id;
        this.remarks.value.value = acquisition.remarks || "";
        this.status.value.value = acquisition.status;

        // Load existing acquisition assets
        if (acquisition.details && acquisition.details.length > 0) {
          this.acquisitionAssets = acquisition.details.map((detail) => ({
            asset_acquisition_details_id: detail.id,
            product_id: detail.product_id,
            asset_category_id: detail.asset_category_id,
            asset_location_id: detail.asset_location_id,
            asset_depreciation_method_id: detail.asset_depreciation_method_id,
            asset_status_id: detail.asset_status_id,
            purchase_cost: detail.purchase_cost,
            salvage_value: detail.salvage_value,
            useful_life: detail.useful_life,
            quantity: detail.quantity,
            warranty_month: detail.warranty_month,
            depreciation_start_date: detail.depreciation_start_date,
            total_units: detail.total_units || 1,
            per_period_unit: detail.per_period_unit || 0,
          }));
        }
      }
    },
    addAssetRow() {
      this.acquisitionAssets.push({
        asset_acquisition_details_id: null,
        product_id: null,
        asset_category_id: null,
        asset_location_id: null,
        asset_depreciation_method_id: null,
        asset_status_id: null,
        purchase_cost: 0,
        salvage_value: 0,
        useful_life: 1,
        quantity: 1,
        warranty_month: null,
        depreciation_start_date: new Date().toISOString().split('T')[0],
        total_units: 1,
        per_period_unit: 0,
      });
    },
    removeAssetRow(index) {
      this.acquisitionAssets.splice(index, 1);
      toast.success("Asset row removed");
    },
    onDepreciationMethodChange(asset, methodId) {
      const method = this.depreciationMethods.find(m => m.id === methodId);
      if (method && method.short_name === 'UP') {
        // Units of Production method - enable unit fields
        asset.total_units = asset.total_units || 1;
        asset.per_period_unit = asset.per_period_unit || 0;
      }
    },
    goBack() {
      this.$store.commit("UNSET_EDITING_ASSET_ACQUISITION");
      this.$store.commit("UNSET_VIEW_ASSET_ACQUISITION");
      this.$router.push({ name: "acquisition-purchase-records" });
    },
    getProductName(productId) {
      if (!productId) return null;
      const product = this.products.find(p => p.id === productId);
      return product ? `${product.code} - ${product.name}` : null;
    },
    getCategoryName(categoryId) {
      if (!categoryId) return null;
      const category = this.categories.find(c => c.id === categoryId);
      return category ? category.name : null;
    },
    formatCurrency(value) {
      if (!value && value !== 0) return '0.00';
      return parseFloat(value).toFixed(2);
    },
    isUnitsOfProduction(methodId) {
      if (!methodId) return false;
      const method = this.depreciationMethods.find(m => m.id === methodId);
      return method && method.short_name === 'UP';
    },
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

.asset-card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

:deep(.v-input--density-compact) {
  --v-input-control-height: 36px;
}

:deep(.v-card) {
  border-radius: 8px !important;
  overflow: hidden;
}
</style>

